/**
 * services/quant/confidenceCalibration.ts
 *
 * Confidence Calibration Engine — MER Algo Footprints v5.3
 *
 * Evaluates whether the system's confidence scores are empirically calibrated:
 * do higher-confidence decisions produce better outcomes?
 *
 * Pure calculation — no engine changes, no execution logic.
 */

import { AuditRecord, ConfidenceBucket } from '../../../../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConfidenceCalibrationBucketStats {
  bucket:          ConfidenceBucket;
  count:           number;
  winRate:         number | null;
  avgR:            number | null;
  expectancy:      number | null;
  avgMFE:          number | null;
  avgMAE:          number | null;
  calibrationGap:  number | null;   // difference vs prior bucket's avgR (null for first)
  status:          'overconfident' | 'underconfident' | 'well_calibrated' | 'insufficient_data';
}

export interface ConfidenceCalibrationSummary {
  buckets:            ConfidenceCalibrationBucketStats[];
  overallScore:       number | null;   // 0–100: how well-calibrated overall
  monotonicityScore:  number | null;   // 0–100: penalises bucket inversions
  overconfidenceRisk: 'low' | 'medium' | 'high' | 'unknown';
  summary:            string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BUCKETS: ConfidenceBucket[] = ['0-39', '40-54', '55-69', '70-84', '85+'];
const MIN_SAMPLE = 3;   // minimum closed records to compute meaningful stats

// ─── Helpers ──────────────────────────────────────────────────────────────────

function closed(records: AuditRecord[]): AuditRecord[] {
  return records.filter(r => r.status === 'closed' && r.realizedR !== null);
}

function safeWinRate(recs: AuditRecord[]): number | null {
  const decisive = recs.filter(r => r.outcome === 'win' || r.outcome === 'loss');
  if (!decisive.length) return null;
  return decisive.filter(r => r.outcome === 'win').length / decisive.length;
}

function safeAvgR(recs: AuditRecord[]): number | null {
  const withR = recs.filter(r => r.realizedR !== null);
  if (!withR.length) return null;
  return withR.reduce((s, r) => s + r.realizedR!, 0) / withR.length;
}

function safeExpectancy(recs: AuditRecord[]): number | null {
  const wr = safeWinRate(recs);
  if (wr === null) return null;
  const wins   = recs.filter(r => r.outcome === 'win').map(r => r.realizedR ?? 0);
  const losses = recs.filter(r => r.outcome === 'loss').map(r => r.realizedR ?? 0);
  if (!wins.length || !losses.length) return safeAvgR(recs);
  const avgWin  = wins.reduce((s, v) => s + v, 0) / wins.length;
  const avgLoss = losses.reduce((s, v) => s + v, 0) / losses.length;
  return wr * avgWin + (1 - wr) * avgLoss;
}

function safeAvgField(recs: AuditRecord[], field: 'mfe' | 'mae'): number | null {
  const withVal = recs.filter(r => r[field] !== null);
  if (!withVal.length) return null;
  return withVal.reduce((s, r) => s + r[field]!, 0) / withVal.length;
}

function bucketStatus(
  avgR: number | null,
  prevAvgR: number | null,
  count: number,
): ConfidenceCalibrationBucketStats['status'] {
  if (count < MIN_SAMPLE) return 'insufficient_data';
  if (avgR === null) return 'insufficient_data';
  if (prevAvgR === null) return 'well_calibrated';   // first bucket — baseline
  const gap = avgR - prevAvgR;
  if (gap >= -0.05) return 'well_calibrated';         // ±0.05R tolerance
  if (gap < -0.30) return 'overconfident';            // significantly worse than prior tier
  return 'underconfident';                            // slightly worse — worth watching
}

// ─── Global monotonicity helper ───────────────────────────────────────────────
// Returns false if any consecutive pair drops by more than the tolerance threshold.
function isGloballyMonotonic(values: number[], tolerance = 0.05): boolean {
  for (let i = 1; i < values.length; i++) {
    if (values[i] < values[i - 1] - tolerance) return false;
  }
  return true;
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function computeConfidenceCalibration(
  records: AuditRecord[],
): ConfidenceCalibrationSummary {
  const cl = closed(records);

  // Build per-bucket stats
  let prevAvgR: number | null = null;
  const buckets: ConfidenceCalibrationBucketStats[] = BUCKETS.map(bucket => {
    const recs = cl.filter(r => r.confidenceBucket === bucket);
    const avgR  = safeAvgR(recs);
    const gap   = prevAvgR !== null && avgR !== null ? avgR - prevAvgR : null;
    const stats: ConfidenceCalibrationBucketStats = {
      bucket,
      count:         recs.length,
      winRate:       safeWinRate(recs),
      avgR,
      expectancy:    safeExpectancy(recs),
      avgMFE:        safeAvgField(recs, 'mfe'),
      avgMAE:        safeAvgField(recs, 'mae'),
      calibrationGap: gap,
      status:        bucketStatus(avgR, prevAvgR, recs.length),
    };
    if (recs.length >= MIN_SAMPLE && avgR !== null) prevAvgR = avgR;
    return stats;
  });

  // Monotonicity score: local transitions + global shape awareness
  const measurable = buckets.filter(b => b.status !== 'insufficient_data' && b.calibrationGap !== null);
  let monotonicityScore: number | null = null;
  if (measurable.length >= 2) {
    const positiveTransitions = measurable.filter(b => (b.calibrationGap ?? 0) >= -0.05).length;
    const localScore = Math.round((positiveTransitions / measurable.length) * 100);

    // Global shape: extract avgR values from measurable buckets in order and check monotonicity
    const measurableAvgRs = measurable
      .map(b => b.avgR)
      .filter((v): v is number => v !== null);
    const globallyMonotonic = measurableAvgRs.length >= 2
      ? isGloballyMonotonic(measurableAvgRs)
      : true;  // not enough to judge, don't penalise

    // Cap at 75 if global shape is not monotonic, regardless of local transitions
    monotonicityScore = globallyMonotonic ? localScore : Math.min(localScore, 75);
  }

  // Overall score: combine monotonicity + proportion of well-calibrated buckets
  const wellCalibrated = buckets.filter(b => b.status === 'well_calibrated').length;
  const calibratable   = buckets.filter(b => b.status !== 'insufficient_data').length;
  let overallScore: number | null = null;
  if (calibratable >= 2) {
    const calibScore = Math.round((wellCalibrated / calibratable) * 100);
    overallScore = monotonicityScore !== null
      ? Math.round((calibScore * 0.5) + (monotonicityScore * 0.5))
      : calibScore;
  }

  // Overconfidence risk: driven by high-confidence bucket underperforming
  const highBucket = buckets.find(b => b.bucket === '85+');
  const midBucket  = buckets.find(b => b.bucket === '55-69');
  let overconfidenceRisk: ConfidenceCalibrationSummary['overconfidenceRisk'] = 'unknown';
  if (highBucket && highBucket.status !== 'insufficient_data') {
    if (highBucket.status === 'overconfident') overconfidenceRisk = 'high';
    else if (highBucket.status === 'underconfident') overconfidenceRisk = 'medium';
    else if (midBucket?.avgR !== null && (highBucket.avgR ?? 0) >= (midBucket?.avgR ?? 0)) {
      overconfidenceRisk = 'low';
    } else {
      overconfidenceRisk = 'medium';
    }
  }

  // Summary text
  const dataMsg = calibratable < 2
    ? 'Insufficient closed records for calibration analysis.'
    : overallScore !== null && overallScore >= 70
    ? `Well-calibrated: higher confidence generally produces better outcomes (score ${overallScore}/100).`
    : overallScore !== null
    ? `Calibration gap detected (score ${overallScore}/100): some confidence tiers underperform lower tiers.`
    : 'Calibration data available but limited.';

  return { buckets, overallScore, monotonicityScore, overconfidenceRisk, summary: dataMsg };
}
